import React, { useState } from 'react';
import { usePortfolio } from '../../context/PortfolioContext';
import { useAuth } from '../../context/AuthContext';
import { RefreshCw, TrendingUp, TrendingDown, Briefcase, DollarSign, BarChart2, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
// @ts-ignore
import PortfolioPnlTable from '../../components/PortfolioPnlTable';
// @ts-ignore
import DiversificationPieChart from '../../components/DiversificationPieChart';
// @ts-ignore
import CreateFirstPortfolio from '../../components/CreateFirstPortfolio';
// @ts-ignore
import Spinner from '../../components/Spinner';

// ── KPI card ──────────────────────────────────────────────────────────────────
interface KpiCardProps {
  label: string;
  value: string;
  sub?: string;
  positive?: boolean | null;
  icon: React.ReactNode;
  accent: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, sub, positive, icon, accent }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-start gap-4 shadow-sm">
    <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${accent}18` }}>
      <span style={{ color: accent }}>{icon}</span>
    </div>
    <div className="min-w-0">
      <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">{label}</div>
      <div className="text-xl font-bold text-slate-900 font-mono leading-none">{value}</div>
      {sub && (
        <div className={`text-xs mt-1 font-semibold ${
          positive === true ? 'text-emerald-600' : positive === false ? 'text-red-500' : 'text-slate-500'
        }`}>
          {sub}
        </div>
      )}
    </div>
  </div>
);

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n: number | null | undefined, prefix = '$') => {
  if (n == null || isNaN(n)) return '--';
  const abs = Math.abs(n);
  const formatted = abs >= 1_000_000
    ? `${(abs / 1_000_000).toFixed(2)}M`
    : abs >= 1_000
    ? `${(abs / 1_000).toFixed(1)}K`
    : abs.toFixed(2);
  return `${n < 0 ? '-' : ''}${prefix}${formatted}`;
};

const fmtPct = (n: number | null | undefined) => {
  if (n == null || isNaN(n)) return null;
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
};

// ── Main component ─────────────────────────────────────────────────────────────
const MyPortfolioView: React.FC = () => {
  const { currentUser } = useAuth();
  const {
    portfolios,
    activePortfolio,
    activePortfolioId,
    activePortfolioSummary,
    isSummaryLoading,
    isLoading,
    handleRecalculate,
  } = usePortfolio();

  const [isRecalculating, setIsRecalculating] = useState(false);

  const onRecalculate = async () => {
    if (!currentUser || !activePortfolioId) return;
    setIsRecalculating(true);
    const toastId = toast.loading('Recalculating portfolio metrics…');
    try {
      await handleRecalculate();
      toast.success('Portfolio recalculated!', { id: toastId });
    } catch (error: any) {
      toast.error(`Failed: ${error.message}`, { id: toastId });
    } finally {
      setIsRecalculating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner />
      </div>
    );
  }

  if (!activePortfolioId || portfolios.length === 0) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <CreateFirstPortfolio />
      </div>
    );
  }

  const s = activePortfolioSummary;
  const positions: any[] = s?.pnl_by_ticker || s?.positions || [];
  const posCount = positions.length;

  // Derived metrics
  const totalValue = s?.total_portfolio_value ?? 0;
  const totalPnl = s?.total_pnl ?? 0;
  const dailyPnl = s?.total_daily_pnl ?? 0;
  const costBasis = totalValue - totalPnl;
  const totalPnlPct = costBasis > 0 ? (totalPnl / costBasis) * 100 : null;
  const dailyPnlPct = totalValue > 0 ? (dailyPnl / (totalValue - dailyPnl)) * 100 : null;

  // Top mover
  const topMover = positions.length > 0
    ? [...positions].sort((a: any, b: any) => Math.abs(b.pnl ?? 0) - Math.abs(a.pnl ?? 0))[0]
    : null;

  return (
    <div className="h-full overflow-y-auto bg-[#F0F2F5]">
      {/* ── Page header ── */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Briefcase size={20} className="text-blue-500" />
            {activePortfolio?.name || 'My Portfolio'}
          </h1>
          <p className="text-xs text-slate-400 mt-0.5">
            {posCount} position{posCount !== 1 ? 's' : ''} · Last recalculated on demand
          </p>
        </div>
        <button
          onClick={onRecalculate}
          disabled={isRecalculating}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-all ${
            isRecalculating
              ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
              : 'bg-white border-slate-300 text-slate-700 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50'
          }`}
        >
          <RefreshCw size={14} className={isRecalculating ? 'animate-spin' : ''} />
          {isRecalculating ? 'Calculating…' : 'Recalculate'}
        </button>
      </div>

      <div className="p-6 space-y-6 max-w-7xl mx-auto">

        {/* ── KPI strip ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            label="Portfolio Value"
            value={fmt(totalValue)}
            icon={<DollarSign size={18} />}
            accent="#3B82F6"
          />
          <KpiCard
            label="Day P&L"
            value={fmt(dailyPnl)}
            sub={fmtPct(dailyPnlPct) ?? undefined}
            positive={dailyPnl >= 0 ? true : false}
            icon={dailyPnl >= 0 ? <TrendingUp size={18} /> : <TrendingDown size={18} />}
            accent={dailyPnl >= 0 ? '#10B981' : '#EF4444'}
          />
          <KpiCard
            label="Total P&L"
            value={fmt(totalPnl)}
            sub={fmtPct(totalPnlPct) ?? undefined}
            positive={totalPnl >= 0 ? true : false}
            icon={<BarChart2 size={18} />}
            accent={totalPnl >= 0 ? '#10B981' : '#EF4444'}
          />
          <KpiCard
            label="Positions"
            value={String(posCount)}
            sub={topMover ? `Largest: ${topMover.ticker}` : undefined}
            positive={null}
            icon={<Briefcase size={18} />}
            accent="#8B5CF6"
          />
        </div>

        {/* ── Main content: positions table + allocation ── */}
        {s ? (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

            {/* Holdings table — takes 2/3 */}
            <div className="xl:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
                <span className="text-sm font-bold text-slate-700">Holdings</span>
                <span className="text-xs text-slate-400 font-mono">{posCount} positions</span>
              </div>
              <PortfolioPnlTable pnlData={positions} />
            </div>

            {/* Allocation sidebar — takes 1/3 */}
            <div className="space-y-4">

              {/* Sector allocation */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-5 py-3.5 border-b border-slate-100">
                  <span className="text-sm font-bold text-slate-700">Sector Allocation</span>
                </div>
                <div className="p-4">
                  {s.diversification && s.diversification.length > 0 ? (
                    <>
                      <DiversificationPieChart data={s.diversification} />
                      <div className="mt-3 space-y-1.5">
                        {s.diversification.slice(0, 5).map((d: any) => (
                          <div key={d.sector} className="flex items-center justify-between text-xs">
                            <span className="text-slate-600 truncate">{d.sector}</span>
                            <span className="font-semibold text-slate-800 ml-2">{d.percentage?.toFixed(1)}%</span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-xs text-slate-400 text-center py-4">No allocation data</p>
                  )}
                </div>
              </div>

              {/* Quick stats */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-3">
                <div className="text-sm font-bold text-slate-700 mb-2">Portfolio Stats</div>
                {[
                  { label: 'Cost Basis', value: fmt(costBasis) },
                  { label: 'Unrealised P&L', value: fmt(totalPnl), colored: true, val: totalPnl },
                  { label: 'Return', value: fmtPct(totalPnlPct) || '--', colored: true, val: totalPnl },
                ].map(row => (
                  <div key={row.label} className="flex justify-between items-center text-xs">
                    <span className="text-slate-500">{row.label}</span>
                    <span className={`font-semibold font-mono ${
                      row.colored && row.val != null
                        ? row.val >= 0 ? 'text-emerald-600' : 'text-red-500'
                        : 'text-slate-800'
                    }`}>{row.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : isSummaryLoading ? (
          <div className="flex items-center justify-center py-20">
            <Spinner />
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-dashed border-slate-300 p-10 text-center">
            <AlertCircle size={32} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-600 font-medium">No portfolio data yet</p>
            <p className="text-sm text-slate-400 mt-1">Add transactions and click Recalculate to generate metrics.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyPortfolioView;
